import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { refetchPrefs } from '../lib/prefs.js';

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'trustee' | 'invited' | 'public';
  tier: 'private' | 'spouse' | 'family' | 'close_friends' | 'colleagues' | 'public';
  organizationId?: string | null;
}

interface AuthCtx {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

const STORAGE_KEY = 'mdai_session';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { token: string; user: User };
        setToken(parsed.token);
        setUser(parsed.user);
        localStorage.setItem('mdai_token', parsed.token);
      } catch {
        /* ignore */
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, ...(totp ? { totp } : {}) }),
    });
    if (!res.ok) {
      // Surface the specific reason so the login screen can ask for a 2FA code.
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || 'invalid_credentials');
    }
    const data = (await res.json()) as { token: string; user: User };
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem('mdai_token', data.token);
    refetchPrefs();
  }, []);

  const acceptInvite = useCallback(async (inviteToken: string, password: string) => {
    const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/auth/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: inviteToken, password }),
    });
    if (!res.ok) throw new Error('invalid_or_expired_token');
    const data = (await res.json()) as { token: string; user: User };
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem('mdai_token', data.token);
    refetchPrefs();
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('mdai_token');
  }, []);

  // A 401 from any API call (token expired ≤4h, or session revoked) → log out so
  // the user is bounced to the login screen instead of seeing broken pages.
  useEffect(() => {
    const onExpired = () => { setToken(null); setUser(null); localStorage.removeItem(STORAGE_KEY); };
    window.addEventListener('mdai-auth-expired', onExpired);
    return () => window.removeEventListener('mdai-auth-expired', onExpired);
  }, []);

  const value = useMemo(() => ({ user, token, loading, login, acceptInvite, logout }), [user, token, loading, login, acceptInvite, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
